import React from 'react'
import { useHistory } from 'react-router-dom'

import useContainerContext from '../../containerContext'
import { useBreakpoint } from '../../../hooks'
import { formatToLocalTime } from '../../../utils/dates'
import Table, { Column } from '../../table'
import { SortableColumn } from '../../table-plugins/sortable'
import AvatarName from '../../avatarName'
import { isReportProcessing, isReportFailed } from './reportStatus'

const ProcessingRow = (props) => {
  const { rowdata, onClick, role, className = '', ...rest } = props
  const loading = isReportProcessing(rowdata)
  const failed = isReportFailed(rowdata)
  const inactive = loading || failed
  const stateClass = loading ? 'adas-report-row--loading' : failed ? 'adas-report-row--failed' : ''
  return (
    <tr
      {...rest}
      role={inactive ? undefined : role}
      onClick={inactive ? undefined : onClick}
      className={`${className} ${stateClass}`.trim()}
    />
  )
}

const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  return result ? { r: parseInt(result[1], 16), g: parseInt(result[2], 16), b: parseInt(result[3], 16) } : null
}

const getWorkflowBadgeStyle = (stage) => {
  if (!stage || !stage.color) return null
  const rgb = hexToRgb(stage.color)
  if (!rgb) return null
  return {
    backgroundColor: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.12)`,
    color: stage.color,
  }
}

const getWorkflowBadgeClass = (stage) => {
  if (!stage || !stage.name) return 'default'
  const name = stage.name.toLowerCase()
  if (name === 'added') return 'added'
  if (name === 'scheduled') return 'scheduled'
  if (name.includes('dropped') || name.includes('drop')) return 'dropped-off'
  if (name === 'complete' || name === 'completed') return 'complete'
  return 'default'
}

const sourceLabels = {
  Internal: 'Internal',
  manual: 'Internal',
  CCC: 'CCC',
  adasThink: 'CCC',
  Client: 'Client',
  'Client Portal': 'Client',
  'client-portal': 'Client',
  'client-portal-upload': 'Client',
}

const getVehicleImageUrl = (image) => {
  if (!image) return null
  if (image.startsWith('http://') || image.startsWith('https://')) return image
  return `https://mobiletechassets.s3.us-east-2.amazonaws.com/colorCropped/${image.split('/').pop()}`
}

const ReportTable = () => {
  const { data = [], state, setState, pricingByVendor = {} } = useContainerContext()
  const { BREAKPOINT, screenBiggerThan } = useBreakpoint()
  const isBiggerThanMD = screenBiggerThan(BREAKPOINT.MD)
  const history = useHistory()

  const canViewPrice = (report) => {
    const vendorId = report.company && (report.company._id || report.company)
    if (!vendorId) return false
    return !!pricingByVendor[vendorId.toString()]
  }

  if (!data.length) {
    return <p className='adas-empty'>No reports found.</p>
  }

  const goToReport = (report) => {
    if (isReportProcessing(report) || isReportFailed(report)) return
    history.push('/adas-reports/' + report._id)
  }
  const getVendorName = report => (report.vendor && report.vendor.name) || (report.company && report.company.name) || '—'

  // Mobile: card-based layout
  if (!isBiggerThanMD) {
    return (
      <div>
        {data.map(report => {
          const loading = isReportProcessing(report)
          const failed = isReportFailed(report)
          const inactive = loading || failed
          const v = report.vehicle || {}
          const vehicle = [v.year, v.make, v.model].filter(Boolean).join(' ') || '—'
          const vendorName = getVendorName(report)
          const isNew = !report.lastViewedAt
          const stage = report.workflowStage

          return (
            <div
              key={report._id}
              className={`adas-mobile-card${loading ? ' is-processing' : ''}${failed ? ' is-failed' : ''}`}
              onClick={() => { if (!inactive) goToReport(report) }}
            >
              <div className='d-flex justify-content-between align-items-center'>
                <div className='d-flex align-items-center gap-2'>
                  {!inactive && isNew && <span className='unread-badge' />}
                  <AvatarName size='small' value={vendorName} className='me-2' />
                  <strong style={{ fontSize: 14, color: '#2A2A2A' }}>{vendorName}</strong>
                </div>
                <small style={{ color: '#AAAAAA' }}>{formatToLocalTime(report.dateCreated)}</small>
              </div>
              <div style={{ marginTop: 8, fontSize: 12, color: failed ? '#C0392B' : '#2A2A2A' }}>
                {loading
                  ? 'Generating ADAS report. Please wait up to one minute.'
                  : failed
                    ? 'Report generation failed. Please try uploading again.'
                    : vehicle}
              </div>
              <div className='d-flex justify-content-between align-items-center mt-2'>
                <span style={{ fontSize: 14 }}>
                  {!inactive && canViewPrice(report) && report.totalPrice != null
                    ? `$${Number(report.totalPrice).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                    : '—'}
                </span>
                {(loading || failed || (stage && stage.name)) && (
                  <span
                    className={`workflow-badge ${failed ? 'dropped-off' : 'default'}`}
                    style={!inactive ? (getWorkflowBadgeStyle(stage) || undefined) : undefined}
                  >
                    {loading ? 'Generating' : failed ? 'Failed' : stage.name}
                  </span>
                )}
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  // Desktop: shared Table component (matches estimates/invoices pattern)
  return (
    <Table
      data={data}
      rowKey='_id'
      state={state}
      setState={setState}
      className='primary'
      borderless
      onRowClick={goToReport}
      rowComponent={ProcessingRow}
    >
      <Column
        dataKey='vendor.name'
        width='20%'
        header={<u>Vendor</u>}
        render={(value, { rowData }) => {
          if (isReportProcessing(rowData)) {
            return (
              <div className='d-flex align-items-center adas-report-loading-cell'>
                <span className='adas-report-loading-dot me-2' />
                <div className='adas-report-loading-copy'>
                  <div className='adas-report-loading-title'>Processing report...</div>
                  <div className='adas-report-loading-subtitle'>This row will be clickable when generation is complete.</div>
                </div>
              </div>
            )
          }
          if (isReportFailed(rowData)) {
            return (
              <div className='d-flex align-items-center adas-report-failed-cell'>
                <div className='adas-report-loading-copy'>
                  <div className='adas-report-loading-title'>Report generation failed</div>
                  <div className='adas-report-loading-subtitle'>adasThink couldn't process this estimate. Please try uploading again.</div>
                </div>
              </div>
            )
          }
          const vendorName = value || getVendorName(rowData)
          const isNew = !rowData.lastViewedAt
          return (
            <div className='d-flex align-items-center'>
              {isNew && <span className='unread-badge me-2' />}
              <AvatarName size='medium' value={vendorName} className='me-2' />
              <div>{vendorName}</div>
            </div>
          )
        }}
      />
      <SortableColumn
        dataKey='dateCreated'
        width='8%'
        headerLabel={<u>Date</u>}
        render={value => formatToLocalTime(value)}
      />
      <Column
        dataKey='checkIn.insurance.name'
        width='8%'
        header={<u>Insurance</u>}
        render={(value, { rowData }) => {
          if (isReportProcessing(rowData)) return '—'
          const insuranceName = value || (rowData.checkIn && rowData.checkIn.insurance && rowData.checkIn.insurance.name)
          return insuranceName || '—'
        }}
      />
      <Column
        dataKey='source'
        width='8%'
        header={<u>Source</u>}
        render={(value, { rowData }) => {
          if (isReportProcessing(rowData)) return '—'
          return sourceLabels[value] || value || '—'
        }}
      />
      <Column
        dataKey='vehicle'
        width='20%'
        header={<u>Vehicle Info</u>}
        render={(v, { rowData }) => {
          if (isReportProcessing(rowData) || isReportFailed(rowData)) return '—'
          if (!v) return '—'
          const imageUrl = getVehicleImageUrl(v.image)
          return (
            <div className='d-flex align-items-center gap-2 justify-content-center adas-report-vehicle-cell'>
              {imageUrl ? (
                <img src={imageUrl} alt='' style={{ width: 88, height: 66, borderRadius: 8, objectFit: 'cover' }} />
              ) : (
                <div style={{ width: 88, height: 66, borderRadius: 8, background: '#EAEAEA' }} />
              )}
              <div style={{ fontSize: 12, lineHeight: '16px' }}>
                <div>
                  <span>{v.year}</span>{' '}
                  <span style={{ fontWeight: 600 }}>{v.make} {v.model}</span>{' '}
                  {v.color && <span style={{ color: '#5D696F' }}>{v.color}</span>}
                </div>
                <div><span style={{ fontWeight: 600 }}>STOCK/RO:</span> {v.stock || 'Not listed'}</div>
                <div><span style={{ fontWeight: 600 }}>VIN:</span> {v.vin || 'Not listed'}</div>
              </div>
            </div>
          )
        }}
      />
      <SortableColumn
        dataKey='totalPrice'
        width='7%'
        headerLabel={<u>Total</u>}
        render={(value, { rowData }) => {
          if (isReportProcessing(rowData)) return '—'
          if (!canViewPrice(rowData)) return '—'
          if (!value && value !== 0) return '—'
          return `$${Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
        }}
      />
      <Column
        dataKey='clientPortal.calibrationReady'
        width='9%'
        header={<u>Calibration Status</u>}
        render={(value, { rowData }) => {
          if (isReportProcessing(rowData)) return '—'
          const calDate = rowData.clientPortal && rowData.clientPortal.calibrationReadyDate
          if (value === 'yes') return <div><div>READY</div>{calDate && <div style={{ fontSize: 12, color: '#5D696F' }}>{formatToLocalTime(calDate)}</div>}</div>
          if (value === 'no') return 'NOT READY'
          return '—'
        }}
      />
      <Column
        dataKey='workflowStage'
        width='9%'
        header={<u>Workflow Status</u>}
        className='text-center'
        render={(stage, { rowData }) => {
          if (isReportProcessing(rowData)) {
            return <span className='workflow-badge default'>Generating</span>
          }
          if (isReportFailed(rowData)) {
            return <span className='workflow-badge dropped-off'>Failed</span>
          }
          if (!stage || !stage.name) return '—'
          const dynamicStyle = getWorkflowBadgeStyle(stage)
          return (
            <span
              className={`workflow-badge${!dynamicStyle ? ' ' + getWorkflowBadgeClass(stage) : ''}`}
              style={dynamicStyle || undefined}
            >
              {stage.name}
            </span>
          )
        }}
      />
    </Table>
  )
}

export default ReportTable
